import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { createNaturalPerson } from '@dealstack/frontend/app/services/Person.service.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect } from 'react';

export enum CREATE_PERSON_NATURAL_FORM_VALUES {
	FIRST_NAME = 'firstName',
	LAST_NAME = 'lastName',
}

interface CreatePersonNaturalForm {
	[CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME]: string;
	[CREATE_PERSON_NATURAL_FORM_VALUES.LAST_NAME]: string;
}

export const CreatePersonNaturalShape = z.object({
	[CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME]: z
		.string()
		.nonempty({ message: 'First name is required' }),
	[CREATE_PERSON_NATURAL_FORM_VALUES.LAST_NAME]: z
		.string()
		.nonempty({ message: 'Last name is required' }),
});

export const useCreatePersonNaturalController = (
	onCreateSuccess: (createdEntityId: string, withClose?: boolean) => void,
	dealId: string,
	setComputedName
) => {
	const createNaturalPersonRequest = useMutation({
		mutationKey: [API_PATHS.PERSON_DEAL_DEALID_PERSON_CREATE_NATURAL_POST],
		mutationFn: createNaturalPerson,
	});

	const createPersonNaturalForm = useForm<CreatePersonNaturalForm>({
		mode: 'onSubmit',
		resolver: zodResolver(CreatePersonNaturalShape),
		defaultValues: {
			[CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME]: '',
			[CREATE_PERSON_NATURAL_FORM_VALUES.LAST_NAME]: '',
		},
	});

	const watchedFirstName = createPersonNaturalForm.watch(
		CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME
	);
	const watchedLastName = createPersonNaturalForm.watch(
		CREATE_PERSON_NATURAL_FORM_VALUES.LAST_NAME
	);

	useEffect(() => {
		const watchedName = `${watchedFirstName} ${watchedLastName}`.trim();
		setComputedName(watchedName);
	}, [watchedFirstName, watchedLastName, setComputedName]);

	const onSubmit = async (formValues: CreatePersonNaturalForm, resetFormOnSuccess?: boolean) => {
		const { firstName, lastName } = formValues;
		createNaturalPersonRequest.mutate(
			{ dealId, createArgs: { firstName, lastName } },
			{
				onSuccess: (createdEntity) => {
					if (resetFormOnSuccess) {
						createPersonNaturalForm.reset();
					}

					onCreateSuccess(createdEntity.id, !resetFormOnSuccess);
				},
			}
		);
	};

	return { createPersonNaturalForm, onSubmit, isLoading: createNaturalPersonRequest.isPending };
};
